import {
  PRODUCT_LOADER_KEY,
  PRODUCT_REMOVE_LOADER_KEY
} from '@modules/order/constants/uiConstants'
import { OrderKPIByTypes } from '@modules/order/stores/order/OrderDataStore'
import { filterValueMapper } from '@modules/order/utils/filter-value-mapper'
import { CancellableRequest } from '@services/HttpService'
import debounce from 'lodash/debounce'
import concat from 'lodash/concat'
import findIndex from 'lodash/findIndex'
import forEach from 'lodash/forEach'
import intersectionBy from 'lodash/intersectionBy'
import isEqual from 'lodash/isEqual'
import map from 'lodash/map'
import pullAt from 'lodash/pullAt'
import sumBy from 'lodash/sumBy'
import { action, computed, observable, runInAction } from 'mobx'
import { computedFn } from 'mobx-utils'
import { FilterStore } from './FilterStore'
import { SortStore } from './SortStore'
import { IndexObjectStore } from './IndexObjectStore'
import { EnhancedSortStore } from './EnhancedSortStore'
import { CNFProductFilterStore } from '@modules/showroom/CNFProductFilter/store/CNFProductFilterStore'
import { CNFGroupByUIStore } from '@modules/showroom/CNFGroupBy/stores/CNFGroupByUIStore'
import {
  addParentsGroupData,
  removeSkippedGroups
} from '@modules/order/utils/dynamic-group-mapping'

export enum HIERARCHY_API_KEY {
  HEIRARCHY = 'HEIRARCHY',
  FILTER_ATTRIBUTES = 'FILTER_ATTRIBUTES',
  ITEMS = 'ITEMS'
}

/**
 * @TODO Remove product teminology
 */

export abstract class PaginatedHierarchyItemsDataStore {
  /**------HeirarchyManagement fields - start ---- */
  @observable heirarchy = []
  @observable heirarchyLoading = true

  @observable searchKey = ''

  @observable isQuantityUpdated = false

  indexCounter = { start: 0, end: 0 }
  /**------HeirarchyManagement fields - end ------ */
  /**------Product management field - start ------ */
  itemIndexObjectStore = new IndexObjectStore('id')
  filterStore = new FilterStore()
  /**------Product management field - end -------- */
  // private apiService = new OrderOverviewService()

  sortStore = new SortStore()

  enhancedSortStore = new EnhancedSortStore()
  cnfProductFilterStore = new CNFProductFilterStore()
  cnfGroupByUIStore = new CNFGroupByUIStore()

  @observable kpis: OrderKPIByTypes

  apiRequestCache = {
    [HIERARCHY_API_KEY.HEIRARCHY]: [],
    [HIERARCHY_API_KEY.FILTER_ATTRIBUTES]: [],
    [HIERARCHY_API_KEY.ITEMS]: []
  }
  singleDoorResCache = {}

  @computed get totalProducts() {
    return sumBy(this.heirarchy, 'totalProducts')
  }

  constructor(public staticFilterValues) {}

  debouncedFetch = debounce(() => {
    this.fetchHierarchy()
    this.fetchFilterAttributes()
  }, 100)

  //   @computed
  //   get products() {
  //     const products = Array(sumBy(this.heirarchy, 'totalProducts')).fill(null)
  //     products.map((val, index) => this.indexProductMap.get(index))
  //   }

  /**
   * Init method is to initialize data store values
   */
  @action
  async init() {
    /**
     * fixed duplicate calls when screen component is rendered using debounce
     * https://centric8.atlassian.net/browse/GBB-8958
     */
    this.debouncedFetch()
  }

  /**
   * Fetch product by hierachy details. We must load data by pagination instead of data by hierarchy level for case when particular
   * hierachy have huge data which will create issues in processing. so keeping above method to support the logic or better understanding of idea
   *
   * @param activity
   * @param family
   * @param line
   * @param fromIndex
   * @param toIndex
   */
  fetchItems = async (fromIndex: number, toIndex: number) => {
    try {
      this.itemIndexObjectStore.setRangeLoading(
        fromIndex,
        toIndex,
        PRODUCT_LOADER_KEY,
        true
      )
      const i = this.apiRequestCache[HIERARCHY_API_KEY.ITEMS].push(
        this.fetchItemsFromAPI(fromIndex, toIndex)
      )
      let products = await this.apiRequestCache[HIERARCHY_API_KEY.ITEMS][i - 1]
      // this.products.spliceWithArray(fromIndex, products.length, map(products, 'id'))
      this.itemIndexObjectStore.setDataByRange(fromIndex, toIndex, products)
    } catch (e) {
      console.log('e', e)
    } finally {
      this.itemIndexObjectStore.setRangeLoading(
        fromIndex,
        toIndex,
        PRODUCT_LOADER_KEY,
        false
      )
    }
  }

  abstract fetchItemsFromAPI(
    fromIndex: number,
    toIndex: number,
    callBack?: Function
  ): CancellableRequest<any> | Promise<any[]>

  hierarchyInRange = computedFn((start: number, end: number, heirarchy, totalPrev) => {
    for (let i = 0, total = totalPrev; i < heirarchy?.length; i++) {
      if (start >= total && end <= total + heirarchy[i].totalProducts) {
        return concat(
          heirarchy?.[i],
          ...(heirarchy?.[i]?.children?.length
            ? this.hierarchyInRange(start, end, heirarchy?.[i]?.children, total)
            : [])
        )
      }
      total += heirarchy?.[i]?.totalProducts
    }
    return []
  })

  /**
   * This method will check if products data is loading or not based on range
   * @param fromIndex
   * @param toIndex
   */
  isProductsDataLoading = computedFn((fromIndex: number, toIndex: number) => {
    return this.itemIndexObjectStore.isObjectsDataLoading(
      fromIndex,
      toIndex,
      PRODUCT_LOADER_KEY
    )
  }) // hampering

  /**
   * This method will check if products data is present or not based on range
   * @param fromIndex
   * @param toIndex
   */
  hasItemsInRange = computedFn((fromIndex: number, toIndex: number) => {
    return this.itemIndexObjectStore.hasObjectsInRange(fromIndex, toIndex)
  }) // hampering

  abstract fetchHierarchyFromAPI(
    forRefresh?: boolean
  ): CancellableRequest<any> | Promise<any>

  @action
  fetchHierarchy = async () => {
    try {
      this.heirarchyLoading = true
      const i = this.apiRequestCache[HIERARCHY_API_KEY.HEIRARCHY].push(
        this.fetchHierarchyFromAPI()
      )
      const heirarchy = await this.apiRequestCache[HIERARCHY_API_KEY.HEIRARCHY][i - 1]
      runInAction(() => {
        this.heirarchy = heirarchy
        this.heirarchyLoading = false
      })
    } catch (e) {
      console.log('e', e)
      if (!this.isCancelled(e)) {
        this.heirarchyLoading = false
      }
    }
  }

  isCancelled(err) {
    return false
  }

  processNewHierarchy = hierarchy => {
    removeSkippedGroups(hierarchy)
    addParentsGroupData(hierarchy)
  }

  mapFiltersToIds = computedFn(filters => {
    return (this.filterStore.filterAttributes || []).reduce(
      (acc, { displayName, key, values }) => {
        if (!values?.length) {
          return acc
        }
        const filtered = map(
          intersectionBy(
            values,
            map((filters || {})[displayName], label => ({ label })),
            'label'
          ),
          'label'
        )
        return {
          ...acc,
          ...(filtered.length ? { [key]: filtered } : {})
        }
      },
      {
        ...Object.keys(filters || {})
          .filter(key => this.staticFilterValues[key])
          .reduce((acc, key) => {
            if (this.staticFilterValues[key]) {
              if (filters[key]?.length) {
                return { ...acc, [key]: filterValueMapper(key, filters[key]) }
              }
              return acc
            }
            return acc
          }, {})
      }
    )
  }) // not hampering

  @action
  applyFilters = filters => {
    if (!isEqual(this.filterStore.appliedFilters, filters)) {
      this.filterStore.setAppliedFilters(filters)
      this.clearAPIRequestCache(HIERARCHY_API_KEY.HEIRARCHY)
      this.clearAPIRequestCache(HIERARCHY_API_KEY.FILTER_ATTRIBUTES)
      this.clearAPIRequestCache(HIERARCHY_API_KEY.ITEMS)
      this.fetchHierarchy()
      this.itemIndexObjectStore.clearAll()
    }
  }

  @action
  applyNewFilters = () => {
    this.clearAPIRequestCache(HIERARCHY_API_KEY.HEIRARCHY)
    this.clearAPIRequestCache(HIERARCHY_API_KEY.FILTER_ATTRIBUTES)
    this.clearAPIRequestCache(HIERARCHY_API_KEY.ITEMS)
    this.fetchHierarchy()
    this.itemIndexObjectStore.clearAll()
  }

  @action
  applySorting = sortOptions => {
    if (!isEqual(this.sortStore.appliedSortOptions, sortOptions)) {
      this.sortStore.setAppliedSortOptions(sortOptions)
      this.clearAPIRequestCache(HIERARCHY_API_KEY.HEIRARCHY)
      this.clearAPIRequestCache(HIERARCHY_API_KEY.FILTER_ATTRIBUTES)
      this.clearAPIRequestCache(HIERARCHY_API_KEY.ITEMS)
      this.fetchHierarchy()
      this.itemIndexObjectStore.clearAll()
    }
  }

  @action
  applySortingAgain = () => {
    this.sortStore.setAppliedSortOptions(this.sortStore.appliedSortOptions)
    this.clearAPIRequestCache(HIERARCHY_API_KEY.HEIRARCHY)
    this.clearAPIRequestCache(HIERARCHY_API_KEY.FILTER_ATTRIBUTES)
    this.clearAPIRequestCache(HIERARCHY_API_KEY.ITEMS)
    this.fetchHierarchy()
    this.itemIndexObjectStore.clearAll()
  }

  @action
  setQuantityUpdated = value => {
    if (this.sortStore.appliedSortOptions.name) {
      this.isQuantityUpdated = value
    } else {
      this.isQuantityUpdated = false
    }
  }

  @action
  applySearch = key => {
    if (key !== this.searchKey) {
      this.searchKey = key
      this.heirarchy = []
      this.clearAPIRequestCache(HIERARCHY_API_KEY.HEIRARCHY)
      this.clearAPIRequestCache(HIERARCHY_API_KEY.FILTER_ATTRIBUTES)
      this.clearAPIRequestCache(HIERARCHY_API_KEY.ITEMS)
      this.itemIndexObjectStore.clearAll()
      this.fetchHierarchy()
    }
  }

  @action
  reloadPageGroupsAndProducts = () => {
    this.heirarchy = []
    this.clearAPIRequestCache(HIERARCHY_API_KEY.HEIRARCHY)
    this.clearAPIRequestCache(HIERARCHY_API_KEY.FILTER_ATTRIBUTES)
    this.clearAPIRequestCache(HIERARCHY_API_KEY.ITEMS)
    this.itemIndexObjectStore.clearAll()
    this.fetchHierarchy()
  }

  clearAPIRequestCache = (key: HIERARCHY_API_KEY) => {
    forEach(this.apiRequestCache[key], request => {
      request?.cancel?.()
    })
    this.apiRequestCache[key] = []
  }

  @action
  removeHierarchy(heirarchyStr: string[]) {
    this.removeHierarchyRecursively(this.heirarchy, heirarchyStr)
    this.heirarchy = [...this.heirarchy]
  }

  removeHierarchyRecursively(heirarchy: any[], heirarchyStr: string[]) {
    if (!heirarchy) {
      return
    }
    const hierarchyIndex = findIndex(heirarchy, ['name', heirarchyStr[0]]) // 0
    const heirarchyObj = heirarchy[hierarchyIndex] // object

    if (!heirarchyObj) {
      return
    }

    if (heirarchyObj.totalProducts === 1) {
      pullAt(heirarchy, hierarchyIndex)
    } else {
      heirarchy[hierarchyIndex].totalProducts =
        heirarchy[hierarchyIndex].totalProducts - 1
      this.removeHierarchyRecursively(
        heirarchy[hierarchyIndex].children,
        heirarchyStr.slice(1)
      )
    }
  }

  /**
   * Helper method to return start and end index as per dynamic grouping to load the data
   * This method recursively iterates over groups and return start and end index
   */
  getIndexCountData = (data, hierarchyLevels, levelIndex = 0, start = 0, end = 0) => {
    const searchText = hierarchyLevels[levelIndex]
    const index = data.findIndex(el => el.name === searchText)
    const mains = data.slice(0, index)
    const totalProducts = mains.reduce((acc, el) => {
      return acc + el.totalProducts
    }, 0)
    if (index < 0) {
      this.indexCounter = { start: start + totalProducts, end: end + totalProducts }
      return
    }
    const isChildPresent = !!hierarchyLevels[levelIndex + 1]
    if (data[index].children?.length && isChildPresent) {
      this.getIndexCountData(
        data[index].children,
        hierarchyLevels,
        levelIndex + 1,
        start + totalProducts,
        end + totalProducts
      )
    } else {
      this.indexCounter = {
        start: start + totalProducts,
        end: end + totalProducts + data[index].totalProducts
      }
      return
    }
  }

  getIndexesFromGroups = (allHierarchyData, hierarchyLevels) => {
    this.indexCounter = { start: 0, end: 0 }
    this.getIndexCountData(allHierarchyData, hierarchyLevels)
    return this.indexCounter
  }

  /**
   * Helper method to return start and end index as per heirarchy to load the data which mimics pagination for now
   * Should be removed when pagination is implemented
   */
  getIndexesFromHierarchy = computedFn(
    (heirarchy: string[], enhancedViewEnabled = false) => {
      if (enhancedViewEnabled) {
        return this.getIndexesFromGroups(this.heirarchy, heirarchy)
      }
      // find activity index
      const activityIndex = findIndex(this.heirarchy, ({ name }) =>
        heirarchy.includes(name)
      )
      const activityTotalProducts = sumBy(
        this.heirarchy.slice(0, activityIndex),
        'totalProducts'
      )
      const familyIndex = findIndex(this.heirarchy[activityIndex]?.children, ({ name }) =>
        heirarchy.includes(name)
      )
      // if family is not present return activity totalProducts
      if (familyIndex === -1) {
        return {
          start: activityTotalProducts,
          end: activityTotalProducts + this.heirarchy[activityIndex]?.totalProducts
        }
      }
      const familyTotalProducts = sumBy(
        this.heirarchy[activityIndex]?.children?.slice(0, familyIndex),
        'totalProducts'
      )
      const lineIndex = findIndex(
        this.heirarchy[activityIndex].children[familyIndex].children,
        ({ name }) => heirarchy.includes(name)
      )
      // If line not present return activity + family products
      if (lineIndex === -1) {
        return {
          start: activityTotalProducts + familyTotalProducts,
          end:
            activityTotalProducts +
            familyTotalProducts +
            this.heirarchy[activityIndex].children[familyIndex].totalProducts
        }
      }
      const lineTotalProducts = sumBy(
        this.heirarchy[activityIndex].children[familyIndex].children.slice(0, lineIndex),
        'totalProducts'
      )
      return {
        start: activityTotalProducts + familyTotalProducts + lineTotalProducts,
        end:
          activityTotalProducts +
          familyTotalProducts +
          lineTotalProducts +
          this.heirarchy[activityIndex].children[familyIndex].children[lineIndex]
            .totalProducts
      }
    }
  ) // hampering

  abstract fetchFilterAttributesFromAPI(): CancellableRequest<any> | Promise<any>

  @action
  fetchFilterAttributes = async () => {
    try {
      this.filterStore.setLoading(true)
      const i = this.apiRequestCache[HIERARCHY_API_KEY.FILTER_ATTRIBUTES].push(
        this.fetchFilterAttributesFromAPI()
      )
      const filters = await this.apiRequestCache[HIERARCHY_API_KEY.FILTER_ATTRIBUTES][
        i - 1
      ]
      this.filterStore.setFilterAttributes(filters || [])
    } finally {
      this.filterStore.setLoading(false)
    }
  }

  @action
  async removeItemObjectFromHierarchy(
    index: number,
    resFromCache?: Record<string, any>,
    isProductRemoved?: Boolean
  ) {
    try {
      const productId = this.itemIndexObjectStore.indexObjectMap.get(index)?.id
      if (productId) {
        this.itemIndexObjectStore.setRangeLoading(
          index,
          index + 1,
          PRODUCT_REMOVE_LOADER_KEY,
          true
        )
        const res =
          resFromCache || (isProductRemoved && (await this.removeItemAPI(productId)))
        this.onProductRemove(productId, res)
        //this.fetchFilterAttributes()
        return res
      }
    } catch (e) {
      throw e
    } finally {
      this.itemIndexObjectStore.setRangeLoading(
        index,
        index + 1,
        PRODUCT_REMOVE_LOADER_KEY,
        false
      )
    }
  }

  abstract removeItemAPI(productId: string): Promise<any>
  onProductRemove(productId, apiResponse) {}

  @action
  setKPIs = (kpi: OrderKPIByTypes) => {
    this.kpis = kpi
  }

  @action setSingleDoorResCache = singleDoorRes => {
    this.singleDoorResCache = singleDoorRes
  }

  @action
  reset() {
    this.heirarchy = []
    this.heirarchyLoading = false
    this.filterStore.reset()
    this.itemIndexObjectStore.clearAll()
    this.searchKey = ''
    this.cnfGroupByUIStore.resetAllGroupBy()
    this.enhancedSortStore.resetEnhancedSortData()
    this.cnfProductFilterStore.resetAllFilterData()
  }

  destroy() {
    this.reset()
    // this.clearAPIRequestCache(HIERARCHY_API_KEY.HEIRARCHY)
    // this.clearAPIRequestCache(HIERARCHY_API_KEY.FILTER_ATTRIBUTES)
    // this.clearAPIRequestCache(HIERARCHY_API_KEY.ITEMS)
  }
}
